// frontend/src/api/productService.ts
import apiClient from './index';
import type {
  Product,
  ProductListed,
  ProductCreatePayload,
  ProductUpdatePayload,
  ImageUploadResponse,
  CountResponse
} from '@/types';
import type { AxiosError } from 'axios';
import axios from 'axios';

interface FetchProductsParams {
  skip?: number;
  limit?: number;
  status?: string;
  search?: string; 
}

export const getProducts = async (params?: FetchProductsParams): Promise<ProductListed[]> => {
  try {
    const response = await apiClient.get<ProductListed[]>('/products/', { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching products:", error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string }>;
        if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
    }
    throw new Error("An unknown error occurred while fetching products.");
  }
};

export const getProductsCount = async (): Promise<CountResponse> => {
  try {
    const response = await apiClient.get<CountResponse>('/products/count');
    return response.data;
  } catch (error) {
    console.error("Error fetching products count:", error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string }>;
        if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
    }
    throw new Error("An unknown error occurred while fetching product count.");
  }
};

export const getProductByIdOrSlug = async (idOrSlug: string): Promise<Product> => {
  try {
    const response = await apiClient.get<Product>(`/products/${idOrSlug}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching product ${idOrSlug}:`, error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string }>;
        if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
    }
    throw new Error(`An unknown error occurred while fetching product ${idOrSlug}.`);
  }
};

export const createProduct = async (productData: ProductCreatePayload): Promise<Product> => {
  try {
    const response = await apiClient.post<Product>('/products/', productData);
    return response.data;
  } catch (error) {
    console.error("Error creating product:", error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: any }>;
        if (apiError.response?.data?.detail) {
            if (typeof apiError.response.data.detail === 'string') {
                throw new Error(apiError.response.data.detail);
            } else if (Array.isArray(apiError.response.data.detail)) {
                throw new Error(apiError.response.data.detail.map((e: any) => `${e.loc?.join(' -> ')}: ${e.msg}`).join('; '));
            } else {
                throw new Error(JSON.stringify(apiError.response.data.detail));
            }
        }
    }
    throw new Error("An unknown error occurred while creating the product.");
  }
};

export const updateProduct = async (productId: number, productData: ProductUpdatePayload): Promise<Product> => {
  try {
    const response = await apiClient.put<Product>(`/products/${productId}`, productData);
    return response.data;
  } catch (error) {
    console.error(`Error updating product ${productId}:`, error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: any }>;
         if (apiError.response?.data?.detail) {
            if (typeof apiError.response.data.detail === 'string') {
                throw new Error(apiError.response.data.detail);
            } else if (Array.isArray(apiError.response.data.detail)) {
                throw new Error(apiError.response.data.detail.map((e: any) => `${e.loc?.join(' -> ')}: ${e.msg}`).join('; '));
            } else {
                throw new Error(JSON.stringify(apiError.response.data.detail));
            }
        }
    }
    throw new Error("An unknown error occurred while updating the product.");
  }
};

export const deleteProduct = async (productId: number): Promise<void> => {
  try {
    await apiClient.delete(`/products/${productId}`);
  } catch (error) {
    console.error(`Error deleting product ${productId}:`, error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string }>;
        if (apiError.response?.data?.detail) throw new Error(apiError.response.data.detail);
    }
    throw error;
  }
};

export const uploadProductImage = async (imageFile: File): Promise<ImageUploadResponse> => {
  const formData = new FormData();
  formData.append('image_file', imageFile);

  try {
    const response = await apiClient.post<ImageUploadResponse>('/products/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
     console.error("Error uploading product image:", error);
    if (axios.isAxiosError(error)) {
        const apiError = error as AxiosError<{ detail?: string | {msg: string}[], message?: string }>;
        if (apiError.response?.data) {
            if (apiError.response.data.detail) {
                 if (typeof apiError.response.data.detail === 'string') {
                    throw new Error(apiError.response.data.detail);
                } else if (Array.isArray(apiError.response.data.detail)) {
                    throw new Error(apiError.response.data.detail.map(d => d.msg).join('; '));
                }
            } else if (apiError.response.data.message) {
                throw new Error(apiError.response.data.message);
            }
        }
    }
    throw new Error("An unknown error occurred during product image upload.");
  }
};
